import { useMemo, useState } from 'react';
import { App, Badge, Button, Card, Popover, Select, Space, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FunnelSimple, PencilSimple, Plus } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageBody, PageHeader } from '../../app/AppLayout';
import { DataTable } from '../../components/DataTable';
import { HintBanner, HintToggle, useDismissibleHint } from '../../components/DismissibleHint';
import { ErrorState, LoadingState } from '../../components/PageStates';
import { ApiError, NetworkError } from '../../api/client';
import { catalogApi, catalogKeys } from '../catalog/api';
import { tokens } from '../../theme';
import {
  STATUS_FILTER_OPTIONS,
  TYPE_LABELS,
  TYPE_OPTIONS,
  contentApi,
  contentKeys,
  dateRangeLabel,
  scheduleLabel,
  scopeLabel,
  statusLabel,
  toQuery,
  type Content,
  type ContentType,
  type StatusFilter,
  type UpsertContentPayload,
} from './api';
import { ContentFormModal } from './ContentFormModal';

/**
 * Màu của cột Loại — đây là **phân loại**, không phải trạng thái, nên dùng cam được:
 * ADR-009 chỉ cấm cam mang nghĩa trạng thái.
 */
const TYPE_TAG_COLOR: Record<ContentType, string | undefined> = {
  Offer: 'orange',
  News: undefined,
  Banner: 'blue',
};

export function ContentsPage() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('notArchived');
  const [typeFilter, setTypeFilter] = useState<ContentType | undefined>();
  const [editing, setEditing] = useState<Content | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const archivedHint = useDismissibleHint('contents-archived-filter');

  const query = useMemo(() => toQuery(statusFilter, typeFilter), [statusFilter, typeFilter]);

  const listQuery = useQuery({
    queryKey: contentKeys.list(query),
    queryFn: () => contentApi.list(query),
  });
  const chainsQuery = useQuery({ queryKey: catalogKeys.chains, queryFn: catalogApi.listChains });
  const branchesQuery = useQuery({
    queryKey: catalogKeys.branches,
    queryFn: catalogApi.listBranches,
  });

  const chains = chainsQuery.data ?? [];
  const branches = branchesQuery.data ?? [];

  const reportError = (error: unknown, fallback: string) => {
    message.error(
      error instanceof ApiError || error instanceof NetworkError ? error.message : fallback,
    );
  };

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: contentKeys.all });
  };

  const saveMutation = useMutation({
    mutationFn: (payload: { id?: string; body: UpsertContentPayload }) =>
      payload.id ? contentApi.update(payload.id, payload.body) : contentApi.create(payload.body),
    onSuccess: async (saved) => {
      await invalidate();
      setEditing(null);
      setIsCreating(false);
      // Tạo ra là bản nháp (BR-902), nên nói rõ còn một bước nữa mới tới khách. Không nói thì
      // người đăng đóng màn và tin là nội dung đã lên.
      message.success(
        saved.status === 'Draft'
          ? 'Đã lưu bản nháp. Bấm Xuất bản thì khách mới thấy.'
          : 'Đã lưu nội dung.',
      );
    },
    onError: (error) => reportError(error, 'Không lưu được nội dung.'),
  });

  const publishMutation = useMutation({
    mutationFn: contentApi.publish,
    onSuccess: async () => {
      await invalidate();
      message.success('Đã xuất bản. Khách thấy khi tới đúng ngày và đúng khung giờ.');
    },
    onError: (error) => reportError(error, 'Không xuất bản được nội dung.'),
  });

  const archiveMutation = useMutation({
    mutationFn: contentApi.archive,
    onSuccess: async () => {
      await invalidate();
      message.success('Đã lưu trữ. Khách không còn thấy nội dung này.');
    },
    onError: (error) => reportError(error, 'Không lưu trữ được nội dung.'),
  });

  const busy = publishMutation.isPending || archiveMutation.isPending;

  const confirmArchive = (row: Content) => {
    modal.confirm({
      title: `Lưu trữ “${row.title}”?`,
      content:
        'Khách không còn thấy nội dung này. Bản ghi vẫn giữ lại, không xóa — muốn dùng lại thì xuất bản lần nữa.',
      okText: 'Lưu trữ',
      okButtonProps: { danger: true },
      cancelText: 'Hủy',
      onOk: () => archiveMutation.mutateAsync(row.id),
    });
  };

  const columns: ColumnsType<Content> = [
    {
      title: 'Tiêu đề',
      key: 'title',
      width: 250,

      // Ghim bên trái: bảy cột không vừa cửa sổ hẹp, và bấm một nút ở cụm hành động bên phải
      // làm khung cuộn ngang theo — đo thật trên trình duyệt thì cột tiêu đề trôi hẳn khỏi tầm
      // nhìn, tức người vận hành xác nhận lưu trữ một dòng mà không còn đọc được đó là bài nào.
      fixed: 'left',
      render: (_, row) => (
        <>
          <div style={{ fontWeight: 600 }}>{row.title}</div>
          {row.body && (
            <div
              style={{
                fontSize: 12,
                color: tokens.color.ink3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {row.body}
            </div>
          )}
        </>
      ),
    },
    {
      title: 'Loại',
      dataIndex: 'type',
      width: 120,
      render: (type: ContentType) => <Tag color={TYPE_TAG_COLOR[type]}>{TYPE_LABELS[type]}</Tag>,
    },
    {
      title: 'Phạm vi',
      key: 'scope',
      width: 145,
      render: (_, row) => (
        <span style={{ color: tokens.color.ink2 }}>{scopeLabel(row, chains, branches)}</span>
      ),
    },
    {
      title: 'Khoảng ngày',
      key: 'dateRange',
      width: 160,
      render: (_, row) => <span className="mono">{dateRangeLabel(row)}</span>,
    },
    {
      title: 'Khung giờ trong ngày',
      key: 'schedules',
      width: 190,
      render: (_, row) => {
        if (row.schedules.length === 0) {
          return <span style={{ color: tokens.color.ink3 }}>Cả ngày, mọi ngày</span>;
        }
        return (
          <Space direction="vertical" size={4}>
            {row.schedules.map((schedule, index) => {
              const label = scheduleLabel(schedule);
              return (
                <Space key={index} size={4} wrap>
                  <Tag className="mono">{label.hours}</Tag>
                  <Tag>{label.days}</Tag>
                </Space>
              );
            })}
          </Space>
        );
      },
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 185,
      render: (_, row) => {
        const status = statusLabel(row);
        return <Tag color={status.tone}>{status.label}</Tag>;
      },
    },
    {
      title: '',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, row) => (
        <Space size={4} wrap>
          <Button
            size="small"
            icon={<PencilSimple size={14} />}
            aria-label={`Sửa ${row.title}`}
            onClick={() => setEditing(row)}
          >
            Sửa
          </Button>
          {row.status !== 'Published' && (
            <Button
              size="small"
              type="primary"
              disabled={busy}
              onClick={() => publishMutation.mutate(row.id)}
            >
              {/*
                Bài đã lưu trữ vẫn xuất bản lại được — backend không chặn, và đó là đường duy
                nhất để dùng lại một đợt ưu đãi cũ, vì vòng đời không có DELETE.
              */}
              {row.status === 'Archived' ? 'Xuất bản lại' : 'Xuất bản'}
            </Button>
          )}
          <Button
            size="small"
            danger
            disabled={row.status === 'Archived' || busy}
            onClick={() => confirmArchive(row)}
          >
            Lưu trữ
          </Button>
        </Space>
      ),
    },
  ];

  // Gom bộ lọc vào MỘT nút "Lọc" trong thanh công cụ (như các trang khác). Trạng thái mặc định
  // "notArchived" là mốc trung tính — chỉ tính vào badge khi khác mặc định.
  const activeFilterCount = (statusFilter !== 'notArchived' ? 1 : 0) + (typeFilter ? 1 : 0);

  const resetFilters = () => {
    setStatusFilter('notArchived');
    setTypeFilter(undefined);
  };

  const fieldLabel = (text: string) => (
    <span style={{ display: 'block', fontSize: 12, color: tokens.color.ink2, marginBottom: 4 }}>
      {text}
    </span>
  );

  const filterControl = (
    <Popover
      trigger="click"
      placement="bottomRight"
      content={
        <Space direction="vertical" size={12} style={{ width: 240 }}>
          <div>
            {fieldLabel('Trạng thái')}
            <Select<StatusFilter>
              style={{ width: '100%' }}
              options={STATUS_FILTER_OPTIONS}
              value={statusFilter}
              onChange={setStatusFilter}
              aria-label="Lọc theo trạng thái"
            />
          </div>
          <div>
            {fieldLabel('Loại')}
            <Select
              allowClear
              style={{ width: '100%' }}
              placeholder="Mọi loại"
              options={TYPE_OPTIONS}
              value={typeFilter}
              onChange={setTypeFilter}
              aria-label="Lọc theo loại"
            />
          </div>
          <Button
            type="link"
            size="small"
            onClick={resetFilters}
            disabled={activeFilterCount === 0}
            style={{ padding: 0, alignSelf: 'flex-start' }}
          >
            Đặt lại bộ lọc
          </Button>
        </Space>
      }
    >
      <Badge count={activeFilterCount} size="small" offset={[-4, 2]}>
        <Button icon={<FunnelSimple size={15} aria-hidden />} size="small">
          Lọc
        </Button>
      </Badge>
    </Popover>
  );

  return (
    <>
      <PageHeader
        title="Nội dung và tin tức"
        subtitle="Cấu hình"
        hint={<HintToggle hint={archivedHint} />}
        actions={
          <Button type="primary" icon={<Plus size={16} />} onClick={() => setIsCreating(true)}>
            Đăng nội dung mới
          </Button>
        }
      />
      <PageBody>
        <Typography.Paragraph
          type="secondary"
          style={{ marginTop: 0, marginBottom: tokens.space.sectionGap }}
        >
          Ưu đãi hôm nay và tin tức khác nhau ở cột <b>Loại</b>, không phải hai chỗ nhập khác nhau.{' '}
          <b>Phạm vi chọn mỗi lần đăng</b> — để trống là toàn chuỗi, không suy ra từ vai trò người
          đăng.
        </Typography.Paragraph>

        <HintBanner
          hint={archivedHint}
          gap={16}
          message="Bộ lọc mặc định ẩn phần đã lưu trữ"
          description="Nội dung hết hạn không tự xóa, nên không có bộ lọc thì sau vài tháng danh sách toàn nội dung cũ."
        />

        {listQuery.isPending && <LoadingState />}
        {listQuery.isError && (
          <ErrorState error={listQuery.error} onRetry={() => void listQuery.refetch()} />
        )}
        {listQuery.isSuccess && (
          <Card>
            <DataTable
              rowKey="id"
              size="small"
              columns={columns}
              dataSource={listQuery.data}
              onReload={() => void listQuery.refetch()}
              searchPlaceholder="Tìm tiêu đề, nội dung"
              searchText={(c) => [c.title, c.body].filter(Boolean).join(' ')}
              toolbarExtra={filterControl}
              pagination={{ pageSize: 20, showSizeChanger: false }}
              scroll={{ x: 1250 }}
              locale={{
                emptyText:
                  statusFilter === 'notArchived' && typeFilter === undefined
                    ? 'Chưa có nội dung nào. Tab Ưu đãi của khách sẽ trống tới khi đăng bài đầu tiên.'
                    : 'Không có nội dung nào khớp bộ lọc.',
              }}
            />
          </Card>
        )}
      </PageBody>

      <ContentFormModal
        content={editing}
        isCreating={isCreating}
        chains={chains}
        branches={branches}
        submitting={saveMutation.isPending}
        onClose={() => {
          setEditing(null);
          setIsCreating(false);
        }}
        onSubmit={(body) => {
          if (isCreating) saveMutation.mutate({ body });
          else if (editing) saveMutation.mutate({ id: editing.id, body });
        }}
      />
    </>
  );
}
